import * as React from "react";
import { ArrowSyncCircle24Regular, ChevronDown20Regular, ChevronUp20Regular, Desktop20Regular, Eraser20Regular, Globe20Regular, Person20Regular, PersonCircle32Regular, Power20Regular } from "@fluentui/react-icons";
import { Link } from "react-router-dom";
import { NavItem, NavLink } from 'reactstrap';
import { MEMBERS_PORTAL } from "../../../accounts/Consts";
import { ILookupDictionary } from "../../../lookup/types/ILookupDictionary";
import { setCurrentPage, toggleTopMenuIsActive } from "../../../redux/reducers/HomeReducer";
import { FormatFields } from "../../../redux/types/FormatFields";
import { IEnableModulesConfigurationParametersItem } from "../../../redux/types/IEnableModulesConfigurationParameterItem";
import { IJobState } from "../../../redux/types/IJobState";
import IMembersPortalComponentDataItem from "../../../redux/types/IMembersPortalComponentDataItem";
import { ITabReduxItem } from "../../../redux/types/ITabReduxItem";
import { CLEAR_CACHE, DISPLAY, JOBS_IN_PROGRESS_MODAL, LANGUAGE, LOGOUT, MEMBERS_PORTAL_ACCOUNT_DETAILS, MEMBERS_PORTAL_MY_ACCOUNT, MY_PACKAGES, MY_PACKAGES_PAGE, REPERTOIRE_PAGE_JOBS, SHORTCUT_KEY_MODAL, SHORTCUTS } from "../../../repertoire/Consts";
import { hasAnyValidValue, isNullOrUndefined } from "../../../util/tools";
import { If } from "../../components/If";
import { IMenuItem } from "../../types/IMenuItem";
import { Menu } from "../Menu";
import UserLanguageDropDown from "../UserLanguageDropdown";
import ViewPreferenceDropDown from "../ViewPreferenceDropDown";
import { AppInsightsService } from "../../../telemetry/services/AppInsightsService";
import { CURRENT_USER_EMAIL_KEY, CURRENT_USER_NAME_KEY } from "../../../redux/services/AccountSessionStorage";
import { MembersPortalService } from "../../../redux/services/MembersPortalService";
import { MembersPortalRoles } from "../../../redux/types/MembersPortalRoles";
import { MenuIcon } from "../icons/MenuIcon";
import { ApplicantAccess } from "../../../membersportal/ApplicantAccess";

interface IHeaderProps {
    setCurrentPage: typeof setCurrentPage;
    toggleTopMenuIsActive: typeof toggleTopMenuIsActive;
    menuItems: IMenuItem[];
    currentPage: IMenuItem;
    topMenuIsActive: boolean;
    username: string;
    currentViewAsUserName: string;
    isSignedIn: boolean;
    enabledModules?: IEnableModulesConfigurationParametersItem[];
    displayCustomerLogo: boolean;
    getDisplayCustomerLogo: () => void;
    logoAddresses: string[];
    isLogoRequested: boolean;
    portalType: string;
    client: string;
    customer: string;
    addTab: (tab: ITabReduxItem, portalType: string) => void;
    addWork: (lookups: ILookupDictionary, currentUserNumber: string, formats?: FormatFields[]) => void;
    addSetLists: (lookups: ILookupDictionary, currentUserNumber: string) => void;
    addAgreements: (lookups: ILookupDictionary, currentUserNumber: string, isNew?: boolean, draftAgreementsMaintenanceGeneralDataViewData?: IMembersPortalComponentDataItem) => void;
    addLivePerformance: (lookups: ILookupDictionary, isNew?: boolean, livePerformanceMaintenanceGeneralDataViewData?: IMembersPortalComponentDataItem, formats?: FormatFields[], distributionCode?: string, currentUserNameNumber?: string) => void;
    uploadCueSheets: (currentUserNumber: string) => void;
    currentUserNameNumber?: string;
    currentUserAccountNumber?: string;
    lookupValues?: ILookupDictionary;
    showModal: (
        modalContent: string,
        modalComponentInstance: string,
        modalProps: any,
        displayModalCloseButton: boolean,
        title: string
    ) => void;
    clearAppStateAndReinitialize: (
        portalType: string,
        currentUserEmail?: string
    ) => Promise<void>;
    jobs: IJobState[];
}

interface IHeaderState {
    isLoading: boolean;
    expandedMobileSection: string | null;
}

export class Header extends React.Component<IHeaderProps, IHeaderState> {
    viewData;
    constructor(props: IHeaderProps) {
        super(props);
        this.state = {
            isLoading: true,
            expandedMobileSection: null
        }
    }

    updateHeader = (menuItem: IMenuItem) => {
        this.props.setCurrentPage(menuItem.link);

        document.title = menuItem.label;
    };

    toggleMobileSection = (sectionName: string) => {
        this.setState((state) => ({
            expandedMobileSection: state.expandedMobileSection === sectionName ? null : sectionName
        }));
    };

    toggleMobileMenu = () => {
        this.setState({
            expandedMobileSection: null
        });
        this.props.toggleTopMenuIsActive();
    };

    handleMobileNavigationClick = (event: React.MouseEvent<HTMLDivElement>) => {
        const target = event.target as HTMLElement;
        const action = target.closest(".mobileMenuSectionContent a, .mobileMenuSectionContent button, .mobileMenuActions a");

        if (
            !action ||
            action.getAttribute("aria-disabled") === "true" ||
            action.classList.contains("disabled") ||
            action.closest(".disabled")
        ) {
            return;
        }

        this.toggleMobileMenu();
    };

    componentDidMount() {
        const { menuItems, portalType } = this.props;

        if (portalType === MEMBERS_PORTAL) {
            this.setState(() => ({ isLoading: true }));
            localStorage.setItem('portalType', portalType);
        }

        document.title = portalType === MEMBERS_PORTAL ? "Members Portal" : "Matching Engine";

        if (menuItems && menuItems.length > 0) {
            this.setState(() => ({ isLoading: false }));
        }
    }

    refreshCache = async (e?: React.MouseEvent<HTMLElement>): Promise<void> => {
        e?.preventDefault();
        const {
            clearAppStateAndReinitialize,
            portalType
        } = this.props;
        const currentUserEmail = sessionStorage.getItem(CURRENT_USER_EMAIL_KEY) || undefined;
        const originalPath = window.location.pathname;

        try {
            if (originalPath !== "/") {
                window.history.pushState({}, "", "/");
                window.dispatchEvent(new PopStateEvent("popstate"));
            }
            await clearAppStateAndReinitialize(portalType, currentUserEmail);
        } catch (error) {
            AppInsightsService.trackException(error, {
                properties: {
                    action: "refreshCache",
                    portalType,
                    path: originalPath,
                    hasCurrentUserEmail: !!currentUserEmail,
                },
            });
            console.error("refreshCache failed:", error);
        }
    }

    handleAddTab = () => {
        const { addTab, portalType } = this.props;
        let currentPage: IMenuItem;
        const linkString = portalType === MEMBERS_PORTAL ? '/members/myPackages' : '/repertoire/myPackages';
        currentPage = {
            name: 'myPackages',
            icon: 'cube',
            children: [],
            isEnabled: false,
            label: 'My Packages',
            link: linkString,
            pageHeader: 'null',
            portalType: portalType,
            isNavBarEnabled: true
        }

        const META_DATA_KEY: any = {
            'mypackages': { 'tab': MY_PACKAGES_PAGE, instance: portalType }
        }

        this.updateHeader(currentPage);

        if (META_DATA_KEY[currentPage.name.toLowerCase()])
            addTab(META_DATA_KEY[currentPage.name.toLowerCase()].tab, portalType);
    };

    componentDidUpdate(prevProps: IHeaderProps) {
        const { menuItems, portalType } = this.props;

        if (prevProps.menuItems !== menuItems && menuItems?.length > 0) {
            if (this.state.isLoading) {
                this.setState({ isLoading: false });
            }

            document.title = menuItems.find(
                f => f.name === (portalType === MEMBERS_PORTAL ? 'works' : 'home')
            )?.pageHeader;
        }
    }

    renderHeaderLogo = () => {
        const { portalType, client } = this.props;
        if (portalType) {
            if (portalType === MEMBERS_PORTAL) {
                if (client) {
                    return <img src={`assets/logo-${client.toLowerCase()}.png`} className="MP-header-logo" title="Members Portal" alt="Members Portal" />
                }
                return <img src={`assets/logo-spt.png`} className="MP-header-logo" title="Members Portal" alt="Members Portal" />
            }
            else {
                return <img src="assets/logo-matching-engine.png" title="Spanish Point Technologies" alt="Spanish Point Technologies" />
            }
        }
    }

    processMenuItems(items: IMenuItem[]): IMenuItem[] {
        return items.map(item => {
            let children = item.children ? this.processMenuItems(item.children) : [];
            const hasChildren = children.length > 0;
            const allChildrenDisabled = hasChildren && children.every(child => !child.isEnabled);
            const isEnabled = item.isEnabled && (!hasChildren || !allChildrenDisabled);

            return {
                ...item,
                isEnabled,
                children
            };
        });
    }

    getProcessedMenuItems(menuItems: IMenuItem[], portalType: string, currentUserNameNumber?: string, currentUserAccountNumber?: string): IMenuItem[] {
        const hasValidMemberIdentity = hasAnyValidValue(currentUserNameNumber, currentUserAccountNumber);
        const permissionAliases: Record<string, string[]> = {
            memberStatementsSearch: ["memberStatementsSearch", "memberStatements"],
            openNewCase: ["myCases"]
        };
        const filtered = menuItems.filter(m => m.link !== '/' && m.link !== null && m.isNavBarEnabled !== false && m.name !== "userDropdownMenu" && (portalType !== MEMBERS_PORTAL || m.name !== MEMBERS_PORTAL_MY_ACCOUNT))
            .map(item => {
                const isAccessibleToNewApplicants = ApplicantAccess.isAccessibleToNewApplicants(item.name);
                const isMyAccountMenu = item.name === MEMBERS_PORTAL_MY_ACCOUNT;
                return {
                    ...item,
                    isEnabled: portalType === MEMBERS_PORTAL && isAccessibleToNewApplicants ? true : item.isEnabled,
                    children: item.children?.map(child => {
                        const permissionKeys = permissionAliases[child.name] ?? [child.name];
                        const hasPermission = portalType !== MEMBERS_PORTAL || isAccessibleToNewApplicants || permissionKeys.some(permissionKey => MembersPortalService.hasPermissionForMenu(permissionKey, MembersPortalRoles.FullAccess));
                        const identityAllowed = portalType !== MEMBERS_PORTAL || hasValidMemberIdentity || isAccessibleToNewApplicants;
                        return {
                            ...child,
                            isEnabled: isAccessibleToNewApplicants || ((child.isEnabled || permissionAliases[child.name] !== undefined) && hasPermission && identityAllowed)
                        };
                    }) ?? []
                };
            });

        return this.processMenuItems(filtered);
    }

    render() {
        if (window.location.pathname === "/aadLogin" || window.location.pathname === "/login") {
            return <div className="emptyHeader" />;
        }

        let className: string = "collapse navbar-collapse";
        const {
            currentPage,
            menuItems,
            topMenuIsActive,
            toggleTopMenuIsActive,
            enabledModules,
            displayCustomerLogo,
            logoAddresses,
            getDisplayCustomerLogo,
            isLogoRequested,
            addTab,
            addWork,
            addSetLists,
            addAgreements,
            portalType,
            currentViewAsUserName,
            currentUserNameNumber,
            currentUserAccountNumber,
            customer,
            lookupValues,
            showModal,
            jobs,
            addLivePerformance,
            uploadCueSheets
        } = this.props;
        const { isLoading, expandedMobileSection } = this.state;
        const jobsToDisplay = REPERTOIRE_PAGE_JOBS;
        const displayedJobs = jobs && jobs.filter(j => jobsToDisplay.includes(j.type));
        const jobsLength = displayedJobs.length;

        const isSignedInParsed = sessionStorage.getItem('isSignedIn') === 'true';
        const username = sessionStorage.getItem(CURRENT_USER_NAME_KEY)
        const displayName =
            portalType === MEMBERS_PORTAL ? (currentViewAsUserName || username) : username;
        if (!isSignedInParsed || menuItems == null || menuItems == undefined || menuItems.length === 0)
            return <div className="emptyHeader" data-testid="emptyHeader" />;

        if (topMenuIsActive) {
            className = className + " in";
        }

        if (isSignedInParsed && !isLogoRequested) {
            getDisplayCustomerLogo();
        }

        if (customer != undefined) {
            localStorage.setItem('customer', JSON.stringify(customer))
        }

        const showModalShortCutKeys = () => {
            showModal(SHORTCUT_KEY_MODAL, SHORTCUT_KEY_MODAL, null, true, "Shortcuts keys");
        }

        const handleOpenJobsInProgressModal = () => {
            showModal(JOBS_IN_PROGRESS_MODAL, JOBS_IN_PROGRESS_MODAL, currentPage, true, "Jobs in progress");
        }

        const renderJobsInProgressLink = jobsLength && jobsLength >= 1;

        const renderTitleSection = () => {

            if (isNullOrUndefined(currentPage)) {
                return null;
            }
            else {
                return <div className="titleSection flexRow flexJustifyContentSpaceBetween tabBannerSection">
                    <div className="flexRow flexAlignItemsCenter paddingLeftLarge">
                        <MenuIcon iconKey={currentPage.icon} />
                        <span>{currentPage.pageHeader}</span>
                    </div>
                    <div className="paddingRightLarge">
                        {!!renderJobsInProgressLink && <div className="jobsProgressHeader" onClick={handleOpenJobsInProgressModal} data-testid="header-jobs-in-progress-button"><ArrowSyncCircle24Regular className="icon syncStatusIcon live" aria-hidden="true" /><a>{`${jobsLength} Jobs in Progress`}</a></div>}
                    </div>
                </div>
            }
        }

        const filteredMenuItems = this.getProcessedMenuItems(menuItems, portalType, currentUserNameNumber, currentUserAccountNumber);
        const userMenuDropdown = menuItems.find(m => m.name === "userDropdownMenu");
        const displayMenuItem = portalType === MEMBERS_PORTAL ? userMenuDropdown?.children?.find(m => m.name === DISPLAY) : menuItems.find(m => m.name === DISPLAY);
        const languageMenuItem = userMenuDropdown?.children?.find(m => m.name === LANGUAGE);

        // reorder Drop-Down of my account
        const membersPortalUserMenuItems = userMenuDropdown?.children ? [
            ...userMenuDropdown.children.filter(item => item.name === MEMBERS_PORTAL_MY_ACCOUNT),
            ...userMenuDropdown.children.filter(item => item.name !== MEMBERS_PORTAL_MY_ACCOUNT && item.name !== DISPLAY && item.name !== LANGUAGE && item.name !== LOGOUT && item.name !== CLEAR_CACHE),
            ...userMenuDropdown.children.filter(item => item.name === DISPLAY),
            ...userMenuDropdown.children.filter(item => item.name === LANGUAGE),
            ...userMenuDropdown.children.filter(item => item.name === LOGOUT || item.name === CLEAR_CACHE)
        ] : [];

        const membersPortalMobileMenuItems = filteredMenuItems.filter(item => item.name !== MEMBERS_PORTAL_MY_ACCOUNT);
        const membersPortalAccountTitle = membersPortalUserMenuItems.find(item => item.name === MEMBERS_PORTAL_MY_ACCOUNT);
        const membersPortalAccountMenuItems = membersPortalUserMenuItems.filter(item => item.name !== MEMBERS_PORTAL_MY_ACCOUNT && item.name !== DISPLAY && item.name !== LANGUAGE && item.name !== LOGOUT && item.name !== CLEAR_CACHE);
        const logoutMenuItem = membersPortalUserMenuItems.find(item => item.name === LOGOUT);
        const clearCacheMenuItem = membersPortalUserMenuItems.find(item => item.name === CLEAR_CACHE);

        const renderMobileSection = (name: string, label: string, icon: React.ReactNode, content: React.ReactNode) => {
            const isExpanded = expandedMobileSection === name;

            return (
                <li className={`mobileMenuSection${isExpanded ? " expanded" : ""}`}>
                    <button
                        type="button"
                        className="mobileMenuSectionHeader"
                        onClick={() => this.toggleMobileSection(name)}
                        aria-expanded={isExpanded}
                        data-testid={`header-mobile-navigation-${name}-button`}
                    >
                        <span className="mobileMenuSectionLabel">
                            {icon}
                            <span>{label}</span>
                        </span>
                        {isExpanded ?
                            <ChevronUp20Regular className="mobileMenuChevron" aria-hidden="true" /> :
                            <ChevronDown20Regular className="mobileMenuChevron" aria-hidden="true" />
                        }
                    </button>
                    {isExpanded &&
                        <div className="mobileMenuSectionContent">
                            {content}
                        </div>
                    }
                </li>
            )
        }

        const renderMobileAccountMenuItems = () => {
            return (
                <ul className="mobileMenuSectionItems">
                    {membersPortalAccountMenuItems.map((item) => {
                        if (item.name == MY_PACKAGES) {
                            const isDisabled = !hasAnyValidValue(currentUserNameNumber, currentUserAccountNumber);
                            return (
                                <NavItem key={item.name} className={isDisabled ? "disabled" : ""}>
                                    <NavLink tag={Link} onClick={(e) => { if (isDisabled) { e.preventDefault(); return; } this.handleAddTab(); }} to={isDisabled ? "#" : item.link} aria-disabled={isDisabled} className={isDisabled ? "disabled" : ""} data-testid={`header-mobile-user-menu-${item.name}-link`}>{item.label}</NavLink>
                                </NavItem>
                            )
                        }

                        if (item.name == MEMBERS_PORTAL_ACCOUNT_DETAILS) {
                            const isDisabled = !item.isEnabled && !ApplicantAccess.isAccessibleToNewApplicants(MEMBERS_PORTAL_MY_ACCOUNT);

                            return (
                                <NavItem key={item.name} className={isDisabled ? "disabled" : ""}>
                                    <NavLink
                                        tag={Link}
                                        to={isDisabled ? "#" : item.link}
                                        aria-disabled={isDisabled}
                                        className={isDisabled ? "disabled" : ""}
                                        data-testid={`header-mobile-user-menu-${item.name}-link`}
                                    >
                                        {item.label}
                                    </NavLink>
                                </NavItem>
                            );
                        }

                        return (
                            <NavItem key={item.name}>
                                <NavLink tag={Link} to={item.link} data-testid={`header-mobile-user-menu-${item.name}-link`}>{item.label}</NavLink>
                            </NavItem>
                        )
                    })}
                </ul>
            )
        }

        if (!isLoading)
            return (
                <header className={`container-full ${portalType ? portalType.toLowerCase() : ''}`}>
                    <nav className="navbar navbar-fixed-top" role="navigation">
                        <div className="container-fluid topSection">

                            {/* Brand and toggle get grouped for better mobile display -->*/}
                            <div className="navbar-header">
                                <button type="button" className="navbar-toggle" data-toggle="collapse" data-testid="header-navigation-toggle-button"
                                    onClick={this.toggleMobileMenu}>
                                    <span className="sr-only">Toggle navigation</span>
                                    <span className="icon-bar"></span>
                                    <span className="icon-bar"></span>
                                    <span className="icon-bar"></span>
                                </button>
                                <NavLink tag={Link} className="navbar-brand" to="/" data-testid="header-home-link">
                                    {this.renderHeaderLogo()}
                                </NavLink>
                            </div>

                            <div className={className}>
                                <If condition={menuItems && menuItems.length > 0}>
                                    <Menu items={filteredMenuItems}
                                        updateHeader={this.updateHeader.bind(this)}
                                        enabledModules={enabledModules}
                                        isMembersPortal={portalType === MEMBERS_PORTAL}
                                        customer={customer}
                                        addTab={(tab) => addTab(tab, portalType)}
                                        currentUserNameNumber={currentUserNameNumber}
                                        lookups={lookupValues}
                                        addWork={addWork}
                                        addAgreements={addAgreements}
                                        addSetLists={addSetLists}
                                        addLivePerformance={addLivePerformance}
                                        uploadCueSheets={uploadCueSheets}
                                        className={portalType === MEMBERS_PORTAL ? "desktopHeaderNavigation" : undefined} />
                                </If>


                                {/* Account & Logo -->*/}
                                <ul className={`nav navbar-nav navbar-right${portalType === MEMBERS_PORTAL ? " desktopHeaderNavigation" : ""}`}>
                                    <If condition={portalType !== MEMBERS_PORTAL}>
                                        <ViewPreferenceDropDown menuItem={displayMenuItem} />
                                    </If>

                                    {/* Logo -->*/}
                                    <If condition={displayCustomerLogo === true && logoAddresses !== undefined && logoAddresses.length > 0}>
                                        <div id="logo" className="navbar-header">
                                            {logoAddresses.map((address, idx) => {
                                                return (
                                                    <NavLink tag={Link} className="navbar-brand" to="/" data-testid={`header-customer-logo`}>
                                                        <img src={address} title="EU" alt="EU" />
                                                    </NavLink>
                                                )
                                            })}
                                        </div>
                                    </If>

                                    {/* User Menu -->*/}
                                    <If condition={!!username
                                        && username.length > 0
                                        && !!(userMenuDropdown && userMenuDropdown.children && userMenuDropdown.children.length > 0)
                                        && portalType === MEMBERS_PORTAL} >
                                        <li className="dropdown userMenuDropdown">
                                            <NavLink className="dropdown-toggle" href="#" data-toggle="dropdown" role="button" aria-haspopup="true" aria-expanded="false" data-testid="header-user-menu-button" aria-label="User menu">
                                                <PersonCircle32Regular className="userMenuIcon" aria-hidden="true" />
                                            </NavLink>
                                            <ul className="dropdown-menu">
                                                {membersPortalUserMenuItems.map((item) => {
                                                    if (item.name == MEMBERS_PORTAL_MY_ACCOUNT) {
                                                        return (
                                                            <>
                                                                <NavItem className="userMenuSectionTitle">
                                                                    <h3>{item.label}</h3>
                                                                </NavItem>
                                                                <hr className="userMenuDivider" />
                                                            </>
                                                        )
                                                    }
                                                    if (item.name == DISPLAY) {
                                                        return (
                                                            <ViewPreferenceDropDown key={item.name} menuItem={item} isAccountMenu={true} />
                                                        )
                                                    }
                                                    if (item.name == LANGUAGE) {
                                                        return (
                                                            <UserLanguageDropDown key={item.name} menuItem={item} isAccountMenu={true} />
                                                        )
                                                    }
                                                    if (item.name == LOGOUT) {
                                                        return (
                                                            <>
                                                                <hr className="userMenuDivider" />
                                                                <NavItem>
                                                                    <NavLink tag={Link} to={item.link} data-testid={`header-user-menu-${item.name}-link`}>{item.label}</NavLink>
                                                                </NavItem>
                                                            </>
                                                        )
                                                    }
                                                    if (item.name == MY_PACKAGES) {
                                                        const isDisabled = !hasAnyValidValue(currentUserNameNumber, currentUserAccountNumber);

                                                        return (
                                                            <NavItem className={isDisabled ? "disabled" : ""}>
                                                                <NavLink tag={Link} onClick={(e) => { if (isDisabled) { e.preventDefault(); return; } this.handleAddTab(); }} to={isDisabled ? "#" : item.link} aria-disabled={isDisabled} className={isDisabled ? "disabled" : ""} data-testid={`header-user-menu-${item.name}-link`}>{item.label}</NavLink>
                                                            </NavItem>
                                                        )
                                                    }
                                                    if (item.name == CLEAR_CACHE) {
                                                        return (
                                                            <li>
                                                                <a href="#" onClick={this.refreshCache} data-testid="header-user-menu-clear-cache-button">
                                                                    {item.label}
                                                                </a>
                                                            </li>
                                                        )
                                                    }

                                                    if (item.name == MEMBERS_PORTAL_ACCOUNT_DETAILS) {
                                                        const isDisabled = !item.isEnabled && !ApplicantAccess.isAccessibleToNewApplicants(MEMBERS_PORTAL_MY_ACCOUNT);

                                                        return (
                                                            <NavItem className={isDisabled ? "disabled" : ""}>
                                                                <NavLink
                                                                    tag={Link}
                                                                    to={isDisabled ? "#" : item.link}
                                                                    aria-disabled={isDisabled}
                                                                    className={isDisabled ? "disabled" : ""}
                                                                    data-testid={`header-user-menu-${item.name}-link`}
                                                                >
                                                                    {item.label}
                                                                </NavLink>
                                                            </NavItem>
                                                        );
                                                    }

                                                    return (
                                                        <NavItem>
                                                            <NavLink tag={Link} to={item.link} data-testid={`header-user-menu-${item.name}-link`}>{item.label}</NavLink>
                                                        </NavItem>
                                                    )
                                                })}
                                            </ul>
                                        </li>
                                    </If>
                                    <If condition={!!username && username.length > 0 && portalType !== MEMBERS_PORTAL}>
                                        <li className="dropdown">
                                            <NavLink className="dropdown-toggle" tag={Link} to="/" data-testid="header-user-menu-button">
                                                {username}
                                                <b className="caret" />
                                            </NavLink>
                                            <ul className="dropdown-menu">
                                                {userMenuDropdown.children.map((item) => {
                                                    if (item.name == LOGOUT) {
                                                        return (
                                                            <NavItem>
                                                                <NavLink tag={Link} to={item.link} data-testid={`header-user-menu-${item.name}-link`}>{item.label}</NavLink>
                                                            </NavItem>
                                                        )
                                                    }
                                                    if (item.name == CLEAR_CACHE) {
                                                        return (
                                                            <NavItem>
                                                                <a href="#" onClick={this.refreshCache} data-testid="header-user-menu-clear-cache-button">
                                                                    {item.label}
                                                                </a>
                                                            </NavItem>
                                                        )
                                                    }
                                                    if (item.name == SHORTCUTS) {
                                                        return (
                                                            <NavItem>
                                                                <a onClick={() => showModalShortCutKeys()} data-testid="header-user-menu-shortcuts-button">{item.label}</a>
                                                            </NavItem>
                                                        )
                                                    }
                                                    if (item.name == MY_PACKAGES) {
                                                        return (
                                                            <NavItem>
                                                                <NavLink tag={Link} onClick={() => this.handleAddTab()} to={item.link} data-testid={`header-user-menu-${item.name}-link`}>{item.label}</NavLink>
                                                            </NavItem>
                                                        )
                                                    }
                                                }
                                                )}
                                            </ul>
                                        </li>
                                    </If>
                                </ul>

                                <If condition={portalType === MEMBERS_PORTAL}>
                                    <div className="membersPortalMobileNavigation" onClickCapture={this.handleMobileNavigationClick}>
                                        <ul className="mobileMenuSections mobileUserMenuSections">
                                            {renderMobileSection(
                                                MEMBERS_PORTAL_MY_ACCOUNT,
                                                membersPortalAccountTitle?.label || "My Account",
                                                <MenuIcon iconKey={membersPortalAccountTitle?.icon} />,
                                                renderMobileAccountMenuItems()
                                            )}
                                        </ul>
                                        <Menu items={membersPortalMobileMenuItems}
                                            updateHeader={this.updateHeader.bind(this)}
                                            enabledModules={enabledModules}
                                            isMembersPortal={true}
                                            customer={customer}
                                            addTab={(tab) => addTab(tab, portalType)}
                                            currentUserNameNumber={currentUserNameNumber}
                                            lookups={lookupValues}
                                            addWork={addWork}
                                            addAgreements={addAgreements}
                                            addSetLists={addSetLists}
                                            addLivePerformance={addLivePerformance}
                                            uploadCueSheets={uploadCueSheets}
                                            renderAsMobileAccordion={true}
                                            expandedSection={expandedMobileSection}
                                            toggleSection={this.toggleMobileSection} />

                                        <ul className="mobileMenuSections mobileUserMenuSections">
                                            {displayMenuItem && renderMobileSection(
                                                DISPLAY,
                                                displayMenuItem.label,
                                                <Desktop20Regular className="mobileMenuSectionIcon" aria-hidden="true" />,
                                                <ViewPreferenceDropDown menuItem={displayMenuItem} renderItemsOnly={true} />
                                            )}

                                            {languageMenuItem && renderMobileSection(
                                                LANGUAGE,
                                                languageMenuItem.label,
                                                <Globe20Regular className="mobileMenuSectionIcon" aria-hidden="true" />,
                                                <UserLanguageDropDown menuItem={languageMenuItem} renderItemsOnly={true} />
                                            )}
                                        </ul>

                                        <div className="mobileMenuActions">
                                            {clearCacheMenuItem &&
                                                <button type="button" className="mobileClearCacheButton" onClick={this.refreshCache} data-testid="header-mobile-user-menu-clear-cache-button">
                                                    <Eraser20Regular aria-hidden="true" />
                                                    <span>{clearCacheMenuItem.label}</span>
                                                </button>
                                            }

                                            {logoutMenuItem &&
                                                <NavLink tag={Link} className="mobileLogOutButton" to={logoutMenuItem.link} data-testid="header-mobile-user-menu-logout-link">
                                                    <Power20Regular aria-hidden="true" />
                                                    <span>{logoutMenuItem.label}</span>
                                                </NavLink>
                                            }
                                        </div>
                                    </div>
                                </If>
                            </div>
                        </div>
                        {renderTitleSection()}
                    </nav>
                </header>
            );
        return <div></div>
    }
}